import { AppNavParams } from '@euler/app/Routes';
import { useCurrentUser } from '@euler/app/flows/auth/context';
import {
  isInspectionTemplateNominatedFor,
  useInspectionTemplates,
} from '@euler/app/flows/task/functions';
import { MediaFileUploadController } from '@euler/app/flows/task/functions/task-detail/MediaFileUploadController';
import { kNullVIN } from '@euler/functions/constants';
import { validateExtendedProperties } from '@euler/functions/order/extendedPropertyUtil';
import {
  useInventory,
  useInventoryLookup,
} from '@euler/functions/useInventory';
import { useMostAppropriateInspectionTemplate } from '@euler/functions/useMostAppropriateInspectionTemplate';
import { useStoreConfiguration } from '@euler/functions/useStoreConfiguration';
import {
  InspectionTemplateCustomization,
  OrderDetail,
  ServiceSubject,
} from '@euler/model';
import {
  VehicleInfo,
  VehicleInspectionTask,
  VehicleReceptionOrder,
} from '@euler/model/entity';
import {
  InspectionCategory,
  InspectionTemplateSceneType,
  ServiceSubjectType,
} from '@euler/model/enum';
import { SiteInspectionStaged, TaskBasicInfo } from '@euler/model/task-detail';
import { VehicleServiceInfo } from '@euler/services';
import { useServiceFactory } from '@euler/services/factory';
import { makeDebug } from '@euler/utils';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { StackHeaderMode } from '@react-navigation/stack/lib/typescript/src/types';
import produce, { Draft } from 'immer';
import { nanoid } from 'nanoid/non-secure';
import {
  ReactNode,
  Suspense,
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BehaviorSubject,
  Subject,
  debounceTime,
  interval,
  mergeWith,
  skip,
} from 'rxjs';
import { useTheme } from 'styled-components';
import { OrderNavParams } from './OrderFlow';
import { useOrderContext } from './functions/OrderContext';
import { makeOrderDraftFromContext } from './functions/makeOrderDraftFromContext';
import { useOrderFormController } from './functions/useOrderFormController';
import { WiredOrderDigitalSignModal } from './pages/OrderScreen/components/DigitalSignModal';
import { TermsOfServiceModal } from './pages/OrderScreen/components/TermsOfServiceModal';
import {
  OrderDraft,
  OrderExtra,
  OrderFileUploadRequestContext,
  OrderFlowContextProps,
  OrderFlowStep,
  OrderForm,
  OrderNavigateAwayModalAction,
  OrderOcrImageInfo,
  OrderPageCapabilities,
} from './types';

const kMediaUploadConcurrency = 10;
const debug = makeDebug('order:flow-context', __DEV__);

export const OrderFlowContext = createContext<OrderFlowContextProps>(
  null as any,
);

export const useOrderFlowContext = () => useContext(OrderFlowContext);

export const OrderFlowContextProvider = memo(
  ({
    mode,
    type = 'main',
    defaultValues,
    mainOrderNo,
    basicInfo,
    preInspection,
    capabilities,
    finish,
    headerMode$,
    onNavigateToExistingTask,
    onCreated,
    onUpdated,
    children,
  }: {
    mode: 'new' | 'update';
    type?: 'main' | 'child';
    mainOrderNo?: string;
    basicInfo?: TaskBasicInfo;
    defaultValues?: Partial<OrderForm>;
    preInspection?: SiteInspectionStaged[];
    capabilities?: OrderPageCapabilities;
    children?: ReactNode;
    headerMode$: BehaviorSubject<StackHeaderMode>;
    finish: (callback?: () => void) => void;
    onCreated?: (
      order: VehicleReceptionOrder,
      task: VehicleInspectionTask,
    ) => void;
    onUpdated?: (order: VehicleReceptionOrder) => void;
    onNavigateToExistingTask?: (taskNo: string) => void;
  }) => {
    const navigation =
      useNavigation<StackNavigationProp<AppNavParams & OrderNavParams>>();

    const user = useCurrentUser();

    const { draftManager } = useOrderContext();

    const {
      link: linkColor,
      order: { labelColor, placeholderColor, iconColor },
    } = useTheme();

    const { mediaFileService } = useServiceFactory();

    const theme = useMemo(
      () => ({
        linkColor,
        labelColor,
        placeholderColor,
        iconColor,
      }),
      [iconColor, labelColor, linkColor, placeholderColor],
    );

    const storeConfiguration = useStoreConfiguration();

    // one fetch serves the whole flow: this query returns every template the
    // store can see, whatever its scene, so the facade one is picked out of it
    // rather than queried again.
    const allInspectionTemplates = useInspectionTemplates();

    const inspectionTemplates = useMemo(
      () =>
        allInspectionTemplates.filter(
          isInspectionTemplateNominatedFor.bind(
            null,
            InspectionCategory.Normal,
          ),
        ),
      [allInspectionTemplates],
    );

    const facadeInspectionTemplates = useMemo(
      () =>
        allInspectionTemplates.filter(
          x => x.sceneType === InspectionTemplateSceneType.Facade,
        ),
      [allInspectionTemplates],
    );

    const facadeInspectionTemplate = useMostAppropriateInspectionTemplate(
      facadeInspectionTemplates,
    );

    const orderDetail$ = useCreation(
      () =>
        new BehaviorSubject<OrderExtra>({
          ...basicInfo?.orderDetail,
          // Seed the service subjects for a new order pre-filled from a
          // reservation; basicInfo.orderDetail (edit mode) wins when present.
          serviceSubjects:
            basicInfo?.orderDetail?.serviceSubjects ??
            (defaultValues?.serviceSubjects as ServiceSubject[] | undefined),
          preInspection,
        }),
      [basicInfo?.orderDetail, preInspection, defaultValues?.serviceSubjects],
    );

    const getExtendedPropertiesValidationErrors = usePersistFn(() => {
      return validateExtendedProperties(orderDetail$.value, storeConfiguration);
    });

    const formController = useOrderFormController({
      mode,
      type,
      defaultValues,
      orderDetail$,
      basicInfo,
      storeConfiguration,
      getExtendedPropertiesValidationErrors,
    });

    const licensePlateNoOcrImageInfo$ = useCreation(
      () => new BehaviorSubject<OrderOcrImageInfo | undefined>(undefined),
      [],
    );
    const vinOcrImageInfo$ = useCreation(
      () => new BehaviorSubject<OrderOcrImageInfo | undefined>(undefined),
      [],
    );

    const ocrImageInfo = useMemo(
      () => ({
        licensePlateNo$: licensePlateNoOcrImageInfo$,
        vin$: vinOcrImageInfo$,
      }),
      [licensePlateNoOcrImageInfo$, vinOcrImageInfo$],
    );

    const lastVehicleInfo$ = useCreation(
      () => new BehaviorSubject<VehicleInfo | undefined>(undefined),
      [],
    );

    const vehicleImageUrl$ = useCreation(
      () => new BehaviorSubject<string | undefined>(undefined),
      [],
    );

    const lastServiceInfo$ = useCreation(
      () => new BehaviorSubject<VehicleServiceInfo | undefined>(undefined),
      [],
    );

    const updateDetail = usePersistFn(
      (update: (draft: Draft<OrderDetail>) => void) => {
        const updated = produce(orderDetail$.value, update);
        if (updated !== orderDetail$.value) {
          orderDetail$.next(updated);
        }
      },
    );

    const [inventory] = useInventory();
    const inventoryQuery = useInventoryLookup(inventory);

    const fileUploader = useCreation(
      () =>
        new MediaFileUploadController<OrderFileUploadRequestContext>({
          mediaFileService,
          concurrency: kMediaUploadConcurrency,
        }),
      [mediaFileService],
    );

    const serviceSubjectQuery$ = useCreation(
      () => new BehaviorSubject<string>(''),
      [],
    );

    const showCustomServiceSubjectModal$ = useCreation(
      () => new BehaviorSubject<boolean | ServiceSubject>(false),
      [],
    );

    const showDraftModal$ = useCreation(
      () => new BehaviorSubject<boolean>(false),
      [],
    );

    const skipJobAssignment$ = useCreation(
      () => new BehaviorSubject(false),
      [],
    );

    const draftEnabled = useRef<boolean>(true);
    const [draftId, setDraftId] = useState(nanoid());

    const loadDraft = usePersistFn((draft: OrderDraft) => {
      draftEnabled.current = false;

      showDraftModal$.next(false);
      draftManager.removeDraft(draftId);

      // load draft contents to current form.
      formController.dispatch({ type: 'load', data: draft.form });
      lastVehicleInfo$.next(draft.lastVehicleInfo);
      vehicleImageUrl$.next(draft.vehicleImageUrl);
      ocrImageInfo.licensePlateNo$.next(draft.ocrImageInfo?.licensePlateNo);
      ocrImageInfo.vin$.next(draft.ocrImageInfo?.vin);
      orderDetail$.next(draft.orderDetail ?? {});

      setDraftId(draft.id);
      draftEnabled.current = true;
    });

    const saveDraft = usePersistFn(() => {
      const draft = makeOrderDraftFromContext(value);
      draftManager.saveDraft(draft);
    });

    const discardDraft = usePersistFn(() => {
      draftManager.removeDraft(draftId);
    });

    // dirty check support
    const skipDirtyCheck = useRef(false);
    const navigateAwayAction$ = useCreation(
      () => new Subject<OrderNavigateAwayModalAction>(),
      [],
    );
    const dirty$ = useCreation(() => new BehaviorSubject<boolean>(false), []);
    const { dirty$: formIsDirty$ } = formController;
    useEffect(() => {
      const sub = formIsDirty$
        .pipe(
          skip(1),
          mergeWith(orderDetail$.pipe(skip(1))),
          debounceTime(1000),
        )
        .subscribe(value => {
          if (value === false) {
            dirty$.next(false);
            debug('reset as non-dirty');
          } else {
            dirty$.next(true);
            debug('marked as dirty');
          }
        });
      return () => sub.unsubscribe();
    }, [dirty$, formIsDirty$, orderDetail$]);

    const showTermsOfServiceModal$ = useCreation(
      () => new BehaviorSubject<boolean>(false),
      [],
    );

    const showDigitalSignModal$ = useCreation(
      () => new BehaviorSubject<boolean>(false),
      [],
    );

    const isSignUnavailable$ = useCreation(
      () => new BehaviorSubject<boolean>(false),
      [],
    );

    const step$ = useCreation(
      () => new BehaviorSubject<OrderFlowStep>('order-info'),
      [],
    );

    const isFinished = useRef(false);

    const internalFinish = usePersistFn(
      (options?: {
        simulate?: boolean;
        forceDiscardDraft?: boolean;
        callback?: () => void;
      }) => {
        skipDirtyCheck.current = true;
        isFinished.current = true;
        draftEnabled.current = false;

        // discard the current draft on finish
        if (!options?.simulate && mode === 'new') {
          if (__DEV__ && options?.forceDiscardDraft === false) {
            const draft = makeOrderDraftFromContext(value);
            draft.id = nanoid();
            draftManager.saveDraft(draft);
          }
          discardDraft();
        }

        // ask order flow initiator to finally finish the order flow.
        finish(options?.callback);
      },
    );

    const value = useMemo<OrderFlowContextProps>(
      () => ({
        mode,
        type,
        mainOrderNo,
        capabilities,
        inspectionTemplates,
        facadeInspectionTemplate,
        step$,
        headerMode$,
        draftEnabled,
        draftId,
        navigation,
        storeConfiguration,
        theme,
        dirty$,
        skipJobAssignment$,
        navigateAwayAction$,
        basicInfo,
        formController,
        ocrImageInfo,
        lastVehicleInfo$,
        vehicleImageUrl$,
        lastServiceInfo$,
        orderDetail$,
        skipDirtyCheck,
        updateDetail,
        inventoryQuery,
        fileUploader,
        loadDraft,
        saveDraft,
        discardDraft,
        finish: internalFinish,
        isFinished,
        serviceSubjectQuery$,
        showCustomServiceSubjectModal$,
        showDraftModal$,
        showTermsOfServiceModal$,
        showDigitalSignModal$,
        isSignUnavailable$,
        onNavigateToExistingTask,
        onCreated,
        onUpdated,
      }),
      [
        mode,
        type,
        mainOrderNo,
        capabilities,
        inspectionTemplates,
        facadeInspectionTemplate,
        step$,
        headerMode$,
        draftId,
        navigation,
        storeConfiguration,
        theme,
        dirty$,
        skipJobAssignment$,
        navigateAwayAction$,
        basicInfo,
        formController,
        ocrImageInfo,
        lastVehicleInfo$,
        vehicleImageUrl$,
        lastServiceInfo$,
        orderDetail$,
        skipDirtyCheck,
        updateDetail,
        inventoryQuery,
        loadDraft,
        saveDraft,
        discardDraft,
        serviceSubjectQuery$,
        showCustomServiceSubjectModal$,
        showDraftModal$,
        showTermsOfServiceModal$,
        showDigitalSignModal$,
        isSignUnavailable$,
        fileUploader,
        internalFinish,
        onNavigateToExistingTask,
        onCreated,
        onUpdated,
      ],
    );

    useEffect(() => {
      // save order draft every 3s.
      if (mode === 'update' || type === 'child') return;
      const sub = interval(3000).subscribe(() => {
        if (!draftEnabled.current) {
          return;
        }
        saveDraft();
      });
      return () => {
        debug('order flow disposed');
        sub.unsubscribe();
      };
    }, [saveDraft, draftId, mode, type]);

    const setDefaultInspectionServiceSubjects = usePersistFn(
      (tags: string[]) => {
        if (!tags.length) return;

        const serviceSubjects = orderDetail$.value.serviceSubjects;
        if (serviceSubjects != null && serviceSubjects.length > 0) {
          return;
        }

        const subjectsToAdd: ServiceSubject[] = [];

        for (const template of inspectionTemplates) {
          if (template.customization == null) continue;
          try {
            const customization = JSON.parse(
              template.customization,
            ) as InspectionTemplateCustomization;
            const select = customization?.autoAddToOrderServiceSubjects?.select;
            const assignTo =
              customization?.autoAddToOrderServiceSubjects?.assignTo;
            if (select != null && select.when === 'orderType') {
              if (select.matches.op === 'contains') {
                const tag = select.matches.value?.trim();
                if (tag && tags.includes(tag)) {
                  subjectsToAdd.push({
                    type: ServiceSubjectType.Inspection,
                    templateId: template.cid,
                    name: template.name,
                    assignedTo: assignTo ?? {
                      id: user?.user.memberId ?? 0,
                      name: user?.user.name ?? '',
                    },
                  });
                }
              }
            }
          } catch (err) {
            console.error(err);
          }
        }

        if (subjectsToAdd.length) {
          updateDetail(draft => {
            draft.serviceSubjects = subjectsToAdd;
          });
        }
      },
    );

    const tags = formController.form.tags.value;
    const currentVIN = formController.form.vin.value;

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const setVehicleInfoDefaults = usePersistFn((vin: string) => {
      if (vin === kNullVIN && !formController.form.vehicleInfo.value) {
        formController.form.vehicleInfo.update({
          vehicleBrandName: '未知',
          vehicleName: '未知车型',
          vehicleModel: '未知',
        });
      }
    });

    useEffect(() => {
      if (mode !== 'new') return;
      setDefaultInspectionServiceSubjects(tags ?? []);
    }, [mode, tags, setDefaultInspectionServiceSubjects]);

    useEffect(() => {
      if (mode !== 'new') return;
      setVehicleInfoDefaults(currentVIN);
    }, [mode, currentVIN, setVehicleInfoDefaults]);

    return (
      <Suspense fallback={null}>
        <OrderFlowContext.Provider value={value}>
          <BottomSheetModalProvider>
            {children}
            <TermsOfServiceModal />
            <WiredOrderDigitalSignModal />
          </BottomSheetModalProvider>
        </OrderFlowContext.Provider>
      </Suspense>
    );
  },
);
